import { useCallback, useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Loader2, Plus, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { ClashNote } from "@/components/hr/LeaveCalendar";
import { DateField } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const STATUS_STYLE = {
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  submitted: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  manager_approved: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  cancelled: "border-border bg-muted text-muted-foreground",
};

const label = (s) => s.replace(/_/g, " ");

export const LeaveQueue = ({ period }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [balances, setBalances] = useState(null);
  const [preview, setPreview] = useState(null);
  const [deciding, setDeciding] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [requests, bal, prev] = await Promise.all([
      api.get("/hr/leave/requests"),
      api.get("/hr/leave/balances"),
      api.get(`/hr/leave/payroll-preview/${period}`),
    ]);
    setData(requests.data);
    setBalances(bal.data);
    setPreview(prev.data);
  }, [period]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const decide = async (action) => {
    setBusy("decide");
    try {
      const res = await api.post(`/hr/leave/requests/${deciding.leave_ref}/decide`,
        { action, note });
      toast.success(res.data.message);
      setDeciding(null);
      setNote("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data || !balances || !preview) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading leave</div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="kpi-leave-open" label="Awaiting a decision" value={data.counters.open}
             accent="amber" icon={CalendarDays} />
        <Kpi testId="kpi-leave-mine" label="Waiting on me" value={data.counters.awaiting_me}
             accent={data.counters.awaiting_me ? "amber" : "emerald"} />
        <Kpi testId="kpi-leave-approved" label="Approved this year" value={data.counters.approved}
             accent="teal" />
        <Kpi testId="kpi-leave-deduction" label={`Unpaid deduction ${period}`} accent="violet"
             value={money(preview.total_deduction, "INR", true)}
             sub={`${preview.working_days_in_month} working days`} />
      </div>

      <Panel title="Leave requests" testId="panel-leave-requests"
             kicker="A person's own manager decides; anything long also needs the Chair">
        <Table testId="leave-table" head={<><Th>Reference</Th><Th>Who</Th><Th>Type</Th><Th>Dates</Th>
          <Th>Days</Th><Th>Decides</Th><Th>Status</Th><Th /></>}>
          {data.items.length === 0 && <Empty label="No leave requested yet" colSpan={8} />}
          {data.items.map((r) => (
            <tr key={r.leave_ref} className="border-b border-border/60"
                data-testid={`leave-row-${r.leave_ref}`}>
              <Td className="font-mono text-[11px]">{r.leave_ref}</Td>
              <Td>{r.staff_name}</Td>
              <Td>
                {r.leave_label}
                {!r.paid && <Chip className="ml-1.5 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">unpaid</Chip>}
              </Td>
              <Td className="whitespace-nowrap">{shortDate(r.from_date)} → {shortDate(r.to_date)}</Td>
              <Td className="font-mono text-[11px]">{r.days}</Td>
              <Td className="text-[11.5px] text-muted-foreground">
                {r.status === "manager_approved" ? "President / Chairperson"
                  : (r.manager || "HR")}
              </Td>
              <Td><Chip className={STATUS_STYLE[r.status]}
                        testId={`leave-status-${r.leave_ref}`}>{label(r.status)}</Chip></Td>
              <Td>
                {["submitted", "manager_approved"].includes(r.status) && (
                  <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                          data-testid={`decide-${r.leave_ref}`}
                          onClick={() => { setDeciding(r); setNote(""); }}>
                    Decide
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      {preview.items.length > 0 && (
        <Panel title={`Unpaid leave payroll will deduct — ${period}`} testId="panel-leave-preview"
               kicker="Gross ÷ working days in the month × unpaid days taken">
          <Table testId="leave-preview-table" head={<><Th>Who</Th><Th>Unpaid days</Th>
            <Th>Per day</Th><Th>Deduction</Th><Th>Against</Th></>}>
            {preview.items.map((p) => (
              <tr key={p.staff_ref} className="border-b border-border/60"
                  data-testid={`leave-deduction-${p.staff_ref}`}>
                <Td>{p.name}</Td>
                <Td className="font-mono text-[11px]">{p.unpaid_days}</Td>
                <Td className="font-mono text-[11px]">{money(p.per_day, "INR")}</Td>
                <Td className="font-mono text-[11px]">{money(p.deduction, "INR")}</Td>
                <Td className="font-mono text-[10.5px] text-muted-foreground">
                  {p.refs.join(", ")}
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
      )}

      <Panel title="Balances" testId="panel-leave-balances"
             kicker={`Leave year ${balances.leave_year}`}>
        <Table testId="balances-table" head={<><Th>Who</Th><Th>Manager</Th>
          {(balances.items[0]?.balances || []).map((b) => <Th key={b.code}>{b.label}</Th>)}</>}>
          {balances.items.map((s) => (
            <tr key={s.staff_ref} className="border-b border-border/60"
                data-testid={`balance-row-${s.staff_ref}`}>
              <Td>{s.name}</Td>
              <Td className="text-[11.5px] text-muted-foreground">{s.manager || "—"}</Td>
              {s.balances.map((b) => (
                <Td key={b.code} className="font-mono text-[11px]"
                    data-testid={`balance-${s.staff_ref}-${b.code}`}>
                  {b.remaining}
                  <span className="text-muted-foreground"> / {b.entitled}</span>
                </Td>
              ))}
            </tr>
          ))}
        </Table>
      </Panel>

      <Dialog open={Boolean(deciding)} onOpenChange={(o) => !o && setDeciding(null)}>
        <DialogContent data-testid="leave-decide-dialog">
          <DialogHeader>
            <DialogTitle>{deciding?.leave_ref} — {deciding?.staff_name}</DialogTitle>
            <DialogDescription>
              {deciding?.days} working days of {deciding?.leave_label.toLowerCase()} from{" "}
              {deciding && shortDate(deciding.from_date)} to{" "}
              {deciding && shortDate(deciding.to_date)}.
              {deciding?.needs_chair && deciding?.status === "submitted"
                ? " Because of its length it will still need the Chair after you." : ""}
            </DialogDescription>
          </DialogHeader>
          {deciding && (
            <div className="space-y-3">
              <ClashNote leaveRef={deciding.leave_ref} />
              {deciding.reason && (
                <p className="rounded-xl border border-border bg-muted/25 p-3 text-[12.5px] text-foreground">
                  {deciding.reason}
                </p>
              )}
              {deciding.document_reference && (
                <p className="font-mono text-[11px] text-muted-foreground">
                  Document: {deciding.document_reference}
                </p>
              )}
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">
                  Note (required to refuse — 10 characters or more)
                </Label>
                <Textarea rows={2} value={note} data-testid="leave-decide-note"
                          onChange={(e) => setNote(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="leave-reject"
                    disabled={busy === "decide" || note.trim().length < 10}
                    onClick={() => decide("reject")} className="rounded-full text-[12px]">
              <XCircle className="mr-1.5 h-3.5 w-3.5" /> Not granted
            </Button>
            <Button data-testid="leave-approve" disabled={busy === "decide"}
                    onClick={() => decide("approve")}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const LeavePolicies = () => {
  const { can } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const editable = can("hr", "write");

  const load = useCallback(async () => {
    const res = await api.get("/hr/leave/config");
    setCfg(res.data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/hr/leave/config", {
        types: cfg.types, working_week: cfg.working_week,
        chair_escalation_days: Number(cfg.chair_escalation_days),
        holidays: cfg.holidays, leave_year_starts: cfg.leave_year_starts,
      });
      toast.success("Leave policy saved");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!cfg) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading policy</div>;
  }

  const setType = (i, key, value) => setCfg({
    ...cfg, types: cfg.types.map((t, j) => (j === i ? { ...t, [key]: value } : t)),
  });

  return (
    <div className="space-y-5">
      <Panel title="Leave types" testId="panel-leave-types"
             kicker="Days a year, whether it is paid, how it accrues and what notice is needed">
        <div className="space-y-2">
          {cfg.types.map((t, i) => (
            <div key={t.code} data-testid={`leave-type-${t.code}`}
                 className="grid grid-cols-2 items-end gap-3 rounded-xl border border-border p-3 lg:grid-cols-7">
              <div className="space-y-1">
                <Label className="text-[10.5px] text-muted-foreground">Leave type</Label>
                <Input value={t.label} disabled={!editable} data-testid={`type-label-${t.code}`}
                       onChange={(e) => setType(i, "label", e.target.value)} className="h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10.5px] text-muted-foreground">Days a year</Label>
                <Input type="number" value={t.days_per_year} disabled={!editable}
                       data-testid={`type-days-${t.code}`} className="h-8"
                       onChange={(e) => setType(i, "days_per_year", Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10.5px] text-muted-foreground">Accrual</Label>
                <Select value={t.accrual} disabled={!editable}
                        onValueChange={(v) => setType(i, "accrual", v)}>
                  <SelectTrigger className="h-8" data-testid={`type-accrual-${t.code}`}>
                    <SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Granted in full</SelectItem>
                    <SelectItem value="monthly">Accrues monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10.5px] text-muted-foreground">Carry over cap</Label>
                <Input type="number" value={t.carry_over_cap} disabled={!editable} className="h-8"
                       data-testid={`type-carry-${t.code}`}
                       onChange={(e) => setType(i, "carry_over_cap", Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10.5px] text-muted-foreground">Notice (days)</Label>
                <Input type="number" value={t.notice_days} disabled={!editable} className="h-8"
                       data-testid={`type-notice-${t.code}`}
                       onChange={(e) => setType(i, "notice_days", Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10.5px] text-muted-foreground">Document beyond</Label>
                <Input type="number" value={t.document_required_beyond_days} disabled={!editable}
                       className="h-8" data-testid={`type-document-${t.code}`}
                       onChange={(e) => setType(i, "document_required_beyond_days",
                         Number(e.target.value))} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11.5px] text-muted-foreground">Paid</span>
                <Switch checked={t.paid} disabled={!editable} data-testid={`type-paid-${t.code}`}
                        onCheckedChange={(v) => setType(i, "paid", v)} />
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Rules and holidays" testId="panel-leave-rules"
             kicker="Weekends and these days never come out of anyone's entitlement">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">
                Above how many days does the Chair also sign it off
              </Label>
              <Input type="number" value={cfg.chair_escalation_days} disabled={!editable}
                     data-testid="chair-escalation-input" className="h-9 w-40"
                     onChange={(e) => setCfg({ ...cfg, chair_escalation_days: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">
                Leave year starts (MM-DD)
              </Label>
              <Input value={cfg.leave_year_starts} disabled={!editable} className="h-9 w-40"
                     data-testid="leave-year-input"
                     onChange={(e) => setCfg({ ...cfg, leave_year_starts: e.target.value })} />
            </div>
            <p className="text-[11.5px] text-muted-foreground">
              Working week is Monday to Friday, and this month has{" "}
              <strong className="text-foreground">{cfg.working_days_this_month}</strong> working
              days — the figure payroll divides by for unpaid leave.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-[11.5px] text-muted-foreground">Holiday calendar</Label>
            <div className="max-h-56 space-y-1.5 overflow-auto pr-1" data-testid="holiday-list">
              {(cfg.holidays || []).length === 0 && (
                <p className="text-[12px] text-muted-foreground">No holidays recorded yet.</p>
              )}
              {(cfg.holidays || []).map((h, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`holiday-${i}`}>
                  <DateField value={h.date} clearable={false} testId={`holiday-date-${i}`}
                             className="h-8 w-[140px] text-[11.5px]"
                             onChange={(v) => setCfg({
                               ...cfg,
                               holidays: cfg.holidays.map((x, j) => (j === i ? { ...x, date: v } : x)),
                             })} />
                  <Input value={h.name} className="h-8 flex-1" data-testid={`holiday-name-${i}`}
                         onChange={(e) => setCfg({
                           ...cfg,
                           holidays: cfg.holidays.map((x, j) => (j === i
                             ? { ...x, name: e.target.value } : x)),
                         })} />
                  {editable && (
                    <Button size="sm" variant="outline" className="h-8 rounded-full"
                            data-testid={`holiday-remove-${i}`}
                            onClick={() => setCfg({
                              ...cfg, holidays: cfg.holidays.filter((_, j) => j !== i),
                            })}>×</Button>
                  )}
                </div>
              ))}
            </div>
            {editable && (
              <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                      data-testid="holiday-add"
                      onClick={() => setCfg({
                        ...cfg,
                        holidays: [...(cfg.holidays || []),
                          { date: new Date().toISOString().slice(0, 10), name: "" }],
                      })}>
                <Plus className="mr-1 h-3 w-3" /> Add a holiday
              </Button>
            )}
          </div>
        </div>
        {editable && (
          <Button onClick={save} disabled={busy} data-testid="leave-policy-save"
                  className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save the leave policy
          </Button>
        )}
      </Panel>
    </div>
  );
};
